'use client';

import type {
  InventoryResource,
  Snapshot,
  SourceAccount,
} from '@repo/api-gw-sdk';
import { AccountState, Provider } from '@repo/api-gw-sdk';

import { Loader } from '@/components/layout/loading';
import { useDAL } from '@/contexts/useDAL';
import { useSourceAccounts } from '@/contexts/useSourceAccounts';

import { RedshiftConfiguration } from './steps/redshiftConfiguration';

import type { Encryption } from '../encryption/encryptionComponent';
import type { Step } from '../wizard/Step';
import { Wizard } from '../wizard/Wizard';

export interface RestoreRedshiftState {
  resourceId: string;
  snapshot: Snapshot;
  clusterIdentifier?: string;
  namespaceName?: string;
  nodeType?: string;
  originalNodeType?: string;
  numberOfNodes?: number;
  originalNumberOfNodes?: number;
  vpcSecurityGroupIds: string[];
  port?: number;
  supportedCloudProviders: Provider[];
  restoreAccount?: SourceAccount;
  accounts?: SourceAccount[];
  emptyMessage?: string;
  regionName?: string;
  initialTags: Record<string, string>;
  tags: Record<string, string>;
  keepOriginalTags: boolean;
  encryption?: Encryption;
  initialRegionName: string;
  clusterType: 'provisioned' | 'serverless';
}

const restoreRedshiftFlow: Step<RestoreRedshiftState> = {
  id: 'redshift_configuration',
  Component: RedshiftConfiguration,
};

export const RestoreRedshiftWizard = ({
  resource,
  snapshot,
  onClose,
  onBack,
}: {
  resource: InventoryResource;
  snapshot: Snapshot;
  onClose: () => void;
  onBack: () => void;
}) => {
  const { sourceAccounts } = useSourceAccounts();
  const dal = useDAL();
  const { data: restoreAccountsResponse, isLoading } =
    dal.cloudAccounts.restore.useList();

  const sourceAccount = sourceAccounts?.find(
    (account) =>
      account.providerAccountId === resource.providerAccountId &&
      account.sourceAccountAttributes.cloudProvider === Provider.Aws &&
      account.status === AccountState.ACCOUNT_STATE_CONNECTED
  );

  const hasRestoreAccount = restoreAccountsResponse?.accounts?.some(
    (account) =>
      account.providerAccountId === resource.providerAccountId &&
      account.state === AccountState.ACCOUNT_STATE_CONNECTED
  );

  const allowedAccounts =
    sourceAccount && hasRestoreAccount ? [sourceAccount] : [];

  // Distinguishes provisioned cluster vs serverless namespace forms (different fields)
  const clusterType =
    resource.resourceProperties?.awsRedshift?.clusterType === 'serverless'
      ? ('serverless' as const)
      : ('provisioned' as const);

  if (isLoading || !sourceAccounts || !restoreAccountsResponse) {
    return <Loader />;
  }

  return (
    <Wizard<RestoreRedshiftState>
      onAbort={(origin) => (origin === 'back' ? onBack() : onClose())}
      onFinish={async () => true}
      flow={[restoreRedshiftFlow]}
      initialState={{
        initialRegionName: resource.region || 'us-east-1',
        snapshot,
        resourceId: snapshot.resourceId,
        clusterType,
        originalNodeType: resource.resourceProperties?.awsRedshift?.nodeType,
        originalNumberOfNodes:
          resource.resourceProperties?.awsRedshift?.numberOfNodes,
        supportedCloudProviders: [Provider.Aws],
        restoreAccount: allowedAccounts.length > 0 ? sourceAccount : undefined,
        accounts: allowedAccounts,
        keepOriginalTags: true,
        initialTags: snapshot.resource?.tags || {},
        tags: snapshot.resource?.tags || {},
        vpcSecurityGroupIds: [],
        port: 5439,
        emptyMessage: !sourceAccount
          ? 'The source account for this Redshift resource is not connected. Redshift backups are stored in AWS Backup Vault in the source account and can only be restored to the same account.'
          : !hasRestoreAccount
            ? `A restore account is required for the same AWS account (${resource.providerAccountId}). Please onboard a restore account for this AWS account.`
            : undefined,
      }}
      title='Restore Redshift'
      stepperLabels={['Configuration']}
    />
  );
};
